/* ps-swatch-i18n.js -- Primostore. Authored in the hub, not by Impulse.
   Fixes real defects across TWO separate swatch surfaces that turned out to
   share the same root cause (Shopify's `shopify.color-pattern` taxonomy link
   -- see 08_Brain wiki [[shopify-gotchas]], Abdo declined unlinking it, so
   this rewrites what's RENDERED, never the underlying filter/variant data):

   SURFACE 1 -- the collection-page COLOR filter sidebar (`.color-swatch--filter`,
   snippets/collection-grid-filters-form.liquid, Impulse-native, taxonomy-driven).
   Fixed 2026-08-29 (unchanged below):
     1. Untranslated labels -- both the swatch VALUES (Beige, Black, ...) and
        the filter GROUP header itself ("Color").
     2. A blank swatch. `filter_value.swatch.color.rgb` is Shopify's own
        taxonomy data and is empty for at least "Multicolor" -- the Liquid
        template ships `background-color: rgb();`, invalid CSS the browser
        silently drops, rendering a plain white circle indistinguishable
        from "White".

   SURFACE 2 -- product-card grid swatches (`.grid-product__colors .color-swatch`,
   the small dots under each product on collection/home/search pages and in
   "you may also like" carousels). Added 2026-08-29: Abdo, with real
   screenshots from the live site, showed several of these rendering as
   plain white/hollow circles on primostore.net/collections/water-bottles --
   this surface was explicitly marked OUT OF SCOPE in assets/ps-swatch-
   photos.js's 2026-08-19 docstring (a different fix, for the PDP's own
   variant picker only) and was never covered by anything else. Root cause
   confirmed live: this markup sets `background-color: <slugified option
   value>` directly (e.g. `background-color: rose gold;`), which is valid
   CSS only when the option value happens to already BE a real CSS colour
   keyword (blue, red, black, ...). A genuinely composite name --
   "Pink Granite", "Galaxy Marble", "Blue and Purple", "Turquoise Granite" --
   is not valid CSS, the browser drops it silently, and the circle renders
   blank. Same fix as Surface 1's Multicolor case: detect generically (no
   colour AND no image actually applied), never hardcode a name list, and
   never invent a specific colour for a name this store has no swatch data
   for -- the same multi-hue fallback used for Multicolor communicates
   "multiple/mixed tones" honestly instead of guessing one wrong colour.

   "Clear"/"Transparent" also render as no visible colour by design (that IS
   their real appearance) -- left alone, same as Surface 1's "Clear".

   SURFACE 3 -- the PDP's own variant-picker swatches (`.variant__button-
   label.color-swatch`, the ones a shopper clicks to choose a colour on the
   product page itself). Added 2026-08-29: these do NOT have Surface 2's
   blank-swatch bug -- the theme already renders a real hex per value here,
   confirmed live -- but the Arabic TEXT label is a real, separate gap.
   Checked directly against Shopify's own translation data for a real
   product (BPA Free Water Bottle 1 L): "Black"/"Pink Granite"/"Purple" all
   already have a registered Arabic translation and render correctly:
   Shopify's own per-shop content system, not this script. Only "Blue" was
   missing one on that product -- confirmed via the Admin API that this
   resource type returns no translatable digest to register a fix through
   (same documented limitation as the taxonomy swatches above), so the
   fix is the same as everywhere else in this file: rewrite what's
   RENDERED. Reuses the exact same LABELS_AR map and slug-detection --
   the class name carries the same English slug (`color-swatch--blue`)
   until a real translation exists, at which point the slug itself becomes
   the Arabic word and this naturally stops matching, so it can never
   clobber Shopify's own already-correct translations.

   FIX 2026-08-29 (later same day) -- `translateSwatch`'s text rewrite was
   too blunt for Surface 2. It did `el.textContent = arabic` whenever
   `el.textContent.trim()` was non-empty, which is right for Surfaces 1 and
   3 (the colour name IS the element's own direct text) but wrong for
   Surface 2's real markup, confirmed live:
     <a class="color-swatch ... color-swatch--with-image"
        style="background-color: white;">
       <span class="visually-hidden">White</span>
     </a>
   `el.textContent` on the <a> still reads "White" (textContent counts a
   hidden descendant's text too), so the old code replaced the WHOLE
   `.visually-hidden` span with a bare Arabic text node sitting directly in
   the <a> -- destroying the hidden wrapper and making the Arabic word
   render VISIBLE next to the swatch circle. That is what Abdo saw and
   reported live as "arabic not working well." Fixed by always targeting
   the innermost real text holder -- the `.visually-hidden` child when one
   exists, the element itself otherwise -- so structured markup is never
   collapsed into a bare text node.

   Same fix also made every textContent write idempotent (skip once the
   target already holds the Arabic value). Surface 1's swatches live
   inside the MutationObserver below; per the DOM spec `.textContent =`
   always replaces all of an element's children and fires a childList
   mutation even when the new string equals the old one, so an
   unconditional write would re-trigger that same observer on every one of
   its own passes -- forever, once a shopper toggled a filter on an Arabic
   collection page. Same class of self-inflicted perf problem as the
   body-wide observer removed below, just latent rather than shipped.

   Storefront-wide -- see the {%- if -%} gate in layout/theme.liquid; was
   collection/search only, widened site-wide 2026-08-29 once Surface 2
   started needing to run on the homepage and PDP too. */
(function () {
  "use strict";

  var lang = (document.documentElement.getAttribute("lang") || "en").toLowerCase();
  var isAr = lang.indexOf("ar") === 0;

  /* Keyed by the handleized slug Liquid already emits in the class name
     (`color-swatch--{{ filter_value.label | handle }}`) -- stable and
     whitespace/case-normalized, unlike reading the raw label text back.
     Reviewed by arabic-register 2026-08-29 against real catalog data
     (data/models/listings/*.json's own color `value` fields), not from
     memory: "gold" uses colloquial دهبي (Egyptian shifts ذ->د for this
     word), matching the catalog's own 6-to-3 majority over ذهبي; every
     other entry confirmed already live and correct elsewhere on the site. */
  var LABELS_AR = {
    "beige": "بيج",
    "black": "أسود",
    "blue": "أزرق",
    "bronze": "برونزي",
    "brown": "بني",
    "clear": "شفاف",
    "gold": "دهبي",
    "gray": "رمادي",
    "green": "أخضر",
    "multicolor": "متعدد الألوان",
    "orange": "برتقالي",
    "pink": "وردي",
    "purple": "بنفسجي",
    "red": "أحمر",
    "rose-gold": "روز جولد",
    "silver": "فضي",
    "white": "أبيض",
    "yellow": "أصفر"
  };

  /* A fallback for a swatch Shopify's taxonomy (Surface 1) or the raw option
     value text (Surface 2) gave no usable colour AND no image for. A flat
     gray would misread as an actual "Gray" option, so this uses a small
     multi-hue conic gradient -- reads as "many/mixed colours", not as any
     one specific colour, and never overlaps a real value visually. */
  var FALLBACK_BACKGROUND =
    "conic-gradient(red, orange, yellow, green, blue, purple, red)";

  /* BEM modifier classes that sit alongside the colour modifier on the same
     element but are never themselves a colour -- must be excluded when
     picking the real colour slug out of a multi-class swatch element (e.g.
     `color-swatch color-swatch--small color-swatch--rose-gold color-swatch--with-image`). */
  var NON_COLOR_MODIFIERS = {filter: true, small: true, "with-image": true};

  function swatchSlug(el) {
    var re = /(?:^|\s)color-swatch--([a-z0-9-]+)/g;
    var m;
    while ((m = re.exec(el.className))) {
      if (!NON_COLOR_MODIFIERS[m[1]]) { return m[1]; }
    }
    return null;
  }

  function fixBlankSwatch(el) {
    /* Only when NEITHER a colour nor an image actually applied -- an
       intentionally light colour (e.g. real White/Clear) must be left alone. */
    if (el.style.backgroundImage) { return; }
    if (el.style.backgroundColor) { return; }
    el.style.background = FALLBACK_BACKGROUND;
  }

  function translateSwatch(el) {
    var slug = swatchSlug(el);
    var arabic = slug && LABELS_AR[slug];
    if (!arabic) { return; }
    if (el.title) { el.title = arabic; }
    if (el.textContent.trim()) {
      /* Target the innermost real text holder: a `.visually-hidden` child
         if the swatch has one (Surface 2 -- preserve that wrapper, never
         overwrite the element's own textContent and collapse it), the
         element itself otherwise (Surfaces 1 and 3, plain direct text).
         The `!==` check makes this a no-op once already translated, so it
         never generates a redundant DOM write for the MutationObserver
         below to pick back up (see FIX note in the file header). */
      var hiddenText = el.querySelector(".visually-hidden");
      var target = hiddenText || el;
      if (target.textContent !== arabic) { target.textContent = arabic; }
    }
    if (el.hasAttribute("aria-label")) {
      /* Grid-card swatches (Surface 2) carry "<product title> - <Colour>"
         in aria-label, screen-reader only -- replace just the trailing
         colour word so the product title half is never touched. */
      var label = el.getAttribute("aria-label");
      var idx = label.lastIndexOf(" - ");
      if (idx !== -1) {
        el.setAttribute("aria-label", label.slice(0, idx + 3) + arabic);
      }
    }
    var wrapper = el.closest(".tag__checkbox-wrapper, label");
    var text = wrapper && wrapper.querySelector(".tag__text");
    if (text && text.textContent.trim() && text.textContent !== arabic) {
      text.textContent = arabic;
    }
  }

  /* Added 2026-08-29, Abdo (real screenshots, confirmed twice): an out-of-
     stock colour showing as a blank swatch dot on collection/home page
     product cards, while the product's own page already correctly hides it.
     `color-swatch--unavailable` is added server-side by
     snippets/product-grid-item.liquid's own `{% unless variant.available %}`
     clause (same day) -- this just hides whatever carries it. `display:none`
     rather than removing the element: the row is flexbox with margin-based
     spacing (confirmed live), so a hidden swatch just closes the gap, no
     layout break. Checked FIRST, before anything else in processOne, so an
     unavailable swatch never wastes work getting a colour/translation it
     will never show. */
  function hideUnavailable(el) {
    if (el.classList.contains("color-swatch--unavailable")) {
      el.style.display = "none";
      return true;
    }
    return false;
  }

  function processOne(el) {
    if (hideUnavailable(el)) { return; }
    fixBlankSwatch(el);
    if (isAr) { translateSwatch(el); }
  }

  /* The filter GROUP header ("Color") is a plain text node sitting directly
     inside the trigger button, alongside an icon element from
     'collapsible-icons' -- only the direct text node is touched so the icon
     markup is never at risk. Only one group name is affected by this bug
     (Availability/Price already translate correctly, confirmed live
     2026-08-29 -- they are not taxonomy-linked), so this checks for exactly
     "Color", never a partial/careless match. */
  var GROUP_LABELS_AR = {"color": "اللون"};

  function translateGroupHeader(btn) {
    for (var i = 0; i < btn.childNodes.length; i++) {
      var node = btn.childNodes[i];
      if (node.nodeType !== 3) { continue; }
      var trimmed = node.nodeValue.trim().toLowerCase();
      var arabic = GROUP_LABELS_AR[trimmed];
      if (arabic) { node.nodeValue = node.nodeValue.replace(node.nodeValue.trim(), arabic); }
    }
  }

  function run(root) {
    root = root || document;
    root.querySelectorAll(".color-swatch--filter").forEach(processOne);
    root.querySelectorAll(".grid-product__colors .color-swatch").forEach(processOne);
    root.querySelectorAll(".variant__button-label.color-swatch").forEach(processOne);
    /* SURFACE 4 -- the homepage rail (sections/ps-collection-rail.liquid,
       hub-owned, `.ps-rail__colors` container). Added 2026-08-30: this
       markup was never covered by any selector above, so its own blank-
       swatch cases (composite/marketing colour names on EN, and names with
       no entry in the rail's own normalize table on AR) had no fallback at
       all. Same processOne -- hideUnavailable is a no-op here since the
       rail already omits unavailable variants server-side (never renders
       the <a> at all), fixBlankSwatch supplies the mixed-tone gradient, and
       translateSwatch is a safe no-op too: the rail's class carries the RAW
       (already-localized) value, not an English slug, so swatchSlug's
       ASCII-only regex simply finds nothing to translate. */
    root.querySelectorAll(".ps-rail__colors .color-swatch").forEach(processOne);
    if (isAr) {
      root.querySelectorAll(".collapsible-trigger-btn").forEach(translateGroupHeader);
    }
  }

  if (document.readyState !== "loading") { run(); }
  else { document.addEventListener("DOMContentLoaded", function () { run(); }); }

  /* The filter sidebar/drawer re-renders via AJAX whenever a shopper toggles
     another facet (updated counts) -- without this, the fix would only ever
     apply to the very first paint. Scoped to the two known wrapper ids from
     snippets/collection-grid-filters.liquid rather than the whole document,
     since that is where this facet markup can ever appear. */
  ["CollectionSidebar", "FilterDrawer"].forEach(function (id) {
    var container = document.getElementById(id);
    if (!container || !("MutationObserver" in window)) { return; }
    new MutationObserver(function () { run(container); })
      .observe(container, {childList: true, subtree: true});
  });

  /* A site-wide document.body observer (watching for AJAX grid re-renders --
     infinite scroll, quick-filter swaps) was here briefly on 2026-08-29 and
     REMOVED THE SAME DAY: Abdo reported the site feeling slow right after it
     shipped, and it is a real, credible suspect -- this site already runs
     Judge.me, GTM, Meta Pixel, a chat widget and a gifts app, all mutating
     the DOM independently and often, and a `subtree:true` observer on
     document.body re-fires this script's full querySelectorAll pass on
     every one of those unrelated changes, not just grid re-renders. The
     first `run()` at page load (above) already covers a normal page visit,
     which is the vast majority of real traffic; only the rarer case of a
     shopper triggering an AJAX grid swap without a full navigation goes
     back to being unfixed -- an acceptable trade for not slowing down every
     visit to chase that edge case. */
})();
